import React, { useRef, useMemo, useCallback, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Save, X } from 'lucide-react';
import {
    escapeHtmlForDescription,
    prepareTaskDescriptionHtmlForSave,
} from '../../../utils/task_description_html';
import './TaskDescriptionEditor.css';

const DESCRIPTION_COLORS = ['#1f2937', '#dc2626', '#1e3a5f', '#14532d', '#6366f1'];

function insertChecklistHtml(quill, onHtmlChange) {
    if (!quill) return;
    const range = quill.getSelection(true);
    const index = range ? range.index : Math.max(0, quill.getLength() - 1);
    let lines = [];
    if (range && range.length > 0) {
        const text = quill.getText(range.index, range.length);
        lines = text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
        quill.deleteText(range.index, range.length, 'user');
    }
    const html =
        lines.length > 0
            ? `<ul class="task-desc-checklist">${lines
                  .map((l) => `<li><input type="checkbox"> ${escapeHtmlForDescription(l)}</li>`)
                  .join('')}</ul>`
            : '<ul class="task-desc-checklist"><li><input type="checkbox"> </li></ul>';
    quill.clipboard.dangerouslyPasteHTML(index, html, 'user');
    quill.setSelection(index + 1, 0, 'silent');
    onHtmlChange?.(quill.root.innerHTML);
}

function handleChecklistEnter(e, editorEl, onHtmlChange) {
    if (e.key !== 'Enter' || e.shiftKey) return false;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return false;
    let node = sel.anchorNode;
    if (!node || !editorEl.contains(node)) return false;

    let li = null;
    while (node && node !== editorEl) {
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'LI') {
            const ul = node.closest('ul.task-desc-checklist');
            if (ul) {
                li = node;
                break;
            }
        }
        node = node.parentNode;
    }
    if (!li) return false;

    e.preventDefault();
    e.stopPropagation();

    const doc = editorEl.ownerDocument;
    const range = sel.getRangeAt(0);
    const newLi = doc.createElement('li');
    newLi.innerHTML = '<input type="checkbox"> ';
    const spaceNode = Array.from(newLi.childNodes).find((n) => n.nodeType === Node.TEXT_NODE);

    try {
        const endRange = doc.createRange();
        endRange.setStart(range.endContainer, range.endOffset);
        endRange.setEnd(li, li.childNodes.length);
        const fragment = endRange.extractContents();
        if (fragment.firstChild) newLi.appendChild(fragment);
        if (li.nextSibling) li.parentNode.insertBefore(newLi, li.nextSibling);
        else li.parentNode.appendChild(newLi);
        const target = spaceNode || newLi;
        const offset = spaceNode ? 1 : 0;
        const newRange = doc.createRange();
        newRange.setStart(target, offset);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
    } catch (_) {
        li.parentNode.insertBefore(newLi, li.nextSibling);
    }
    onHtmlChange?.(editorEl.innerHTML);
    return true;
}


/**
 * Editor enriquecido (ReactQuill) para descripción de tarea.
 */
const TaskDescriptionEditor = ({
    value,
    onChange,
    onSave,
    onCancel,
    saving = false,
    placeholder = 'Escribe la descripción de la tarea...',
    editorKey = 'default',
}) => {
    const quillRef = useRef(null);
    const editorWrapperRef = useRef(null);
    const initialHtmlRef = useRef(value || '');
    initialHtmlRef.current = value || '';

    const getEditorHtml = useCallback(() => {
        const quill = quillRef.current?.getEditor?.();
        return quill?.root?.innerHTML ?? '';
    }, []);

    const insertChecklist = useCallback(() => {
        const quill = quillRef.current?.getEditor?.();
        insertChecklistHtml(quill, onChange);
    }, [onChange]);

    const handleSaveClick = useCallback(() => {
        onSave?.(getEditorHtml());
    }, [onSave, getEditorHtml]);

    const modules = useMemo(
        () => ({
            toolbar: {
                container: [
                    [{ header: [2, 3, false] }],
                    ['bold', 'italic', 'underline'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['link'],
                    [{ color: DESCRIPTION_COLORS }],
                    ['checklist'],
                    ['clean'],
                ],
                handlers: {
                    checklist: insertChecklist,
                },
            },
            clipboard: {
                matchVisual: false,
            },
        }),
        [insertChecklist]
    );

    const formats = [
        'header',
        'bold',
        'italic',
        'underline',
        'list',
        'link',
        'color',
    ];

    useEffect(() => {
        const quill = quillRef.current?.getEditor?.();
        if (!quill) return;
        const html = initialHtmlRef.current;
        if (html && quill.root.innerHTML !== html) {
            quill.root.innerHTML = html;
        }

        const onPaste = () => {
            window.setTimeout(() => {
                const cleaned = prepareTaskDescriptionHtmlForSave(quill.root.innerHTML);
                if (cleaned !== quill.root.innerHTML) {
                    quill.root.innerHTML = cleaned;
                }
                onChange(quill.root.innerHTML);
            }, 0);
        };
        quill.root.addEventListener('paste', onPaste);
        return () => quill.root.removeEventListener('paste', onPaste);
    }, [editorKey, onChange]);

    const handleQuillChange = useCallback(
        (_content, _delta, _source, editor) => {
            if (!editor?.root) return;
            onChange(editor.root.innerHTML);
        },
        [onChange]
    );

    useEffect(() => {
        const wrapper = editorWrapperRef.current;
        if (!wrapper) return;
        const editorEl = wrapper.querySelector('.ql-editor');
        if (!editorEl) return;

        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onCancel?.();
                return;
            }
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                onSave?.(editorEl.innerHTML);
                return;
            }
            handleChecklistEnter(e, editorEl, () => {
                onChange(editorEl.innerHTML);
            });
        };

        editorEl.addEventListener('keydown', onKeyDown);
        return () => editorEl.removeEventListener('keydown', onKeyDown);
    }, [onSave, onCancel, onChange]);

    return (
        <div className="task-description-edit-panel" ref={editorWrapperRef}>
            <div className="statistics-description-quill">
                <ReactQuill
                    key={editorKey}
                    ref={quillRef}
                    theme="snow"
                    defaultValue={value || ''}
                    onChange={handleQuillChange}
                    modules={modules}
                    formats={formats}
                    placeholder={placeholder}
                />
            </div>
            <div className="task-description-edit-actions">
                <button
                    type="button"
                    className="btn-edit btn-edit-primary"
                    onClick={handleSaveClick}
                    disabled={saving}
                    aria-label="Guardar descripción"
                >
                    <Save size={14} />
                    {saving ? 'Guardando…' : 'Guardar'}
                </button>
                <button
                    type="button"
                    className="btn-edit btn-edit-secondary"
                    onClick={onCancel}
                    disabled={saving}
                    aria-label="Cancelar edición"
                >
                    <X size={14} />
                    Cancelar
                </button>
            </div>
        </div>
    );
};

export default TaskDescriptionEditor;
